import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Calendar as CalendarIcon, Flag, Star } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getDifficultyLabel = (v) => v <= 3 ? 'Easy' : v <= 6 ? 'Moderate' : v <= 8 ? 'Hard' : 'Brutal';
const getDifficultyColor = (v) => v <= 3 ? 'text-[#22C55E]' : v <= 6 ? 'text-[#CCFF00]' : v <= 8 ? 'text-[#F97316]' : 'text-[#EF4444]';


export default function WODCalendar({ user }) {
  const [wods, setWods] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWod, setSelectedWod] = useState(null);
  const [showWodDialog, setShowWodDialog] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [wodRating, setWodRating] = useState(5);
  const [wodRatingSummary, setWodRatingSummary] = useState(null);
  const [wodRatingSubmitted, setWodRatingSubmitted] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);

  useEffect(() => {
    fetchWods();
    fetchHolidays();
  }, []);

  const fetchWods = async () => {
    try {
      const response = await axios.get(`${API}/wods`);
      setWods(response.data);
    } catch (error) {
      toast.error('Failed to load WODs');
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const year = new Date().getFullYear();
      const [r1, r2] = await Promise.all([
        fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/ZA`),
        fetch(`https://date.nager.at/api/v3/PublicHolidays/${year + 1}/ZA`)
      ]);
      const [h1, h2] = await Promise.all([r1.json(), r2.json()]);
      setHolidays([...h1, ...h2]);
    } catch {
      // Silently fail — holidays are a nice-to-have overlay
    }
  };

  const handleEventClick = (info) => {
    if (info.event.id.startsWith('holiday_')) {
      const date = info.event.id.replace('holiday_', '');
      const holiday = holidays.find(h => h.date === date);
      if (holiday) {
        setSelectedHoliday(holiday);
        setShowHolidayDialog(true);
      }
      return;
    }
    const wod = wods.find(w => w.id === info.event.id);
    if (wod) {
      setSelectedWod(wod);
      setWodRating(5);
      setWodRatingSummary(null);
      setWodRatingSubmitted(false);
      setShowWodDialog(true);
      // Fetch rating summary for this WOD
      axios.get(`${API}/wods/${wod.id}/ratings-summary`)
        .then(res => {
          setWodRatingSummary(res.data);
          if (res.data.user_rating !== null) {
            setWodRating(res.data.user_rating);
            setWodRatingSubmitted(true);
          }
        })
        .catch(() => {});
    }
  };

  const submitWodRating = async () => {
    if (!selectedWod) return;
    setRatingLoading(true);
    try {
      await axios.post(`${API}/ratings`, { wod_id: selectedWod.id, rating: wodRating });
      const res = await axios.get(`${API}/wods/${selectedWod.id}/ratings-summary`);
      setWodRatingSummary(res.data);
      setWodRatingSubmitted(true);
      toast.success('Rating saved!');
    } catch {
      toast.error('Failed to save rating');
    } finally {
      setRatingLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const calendarEvents = wods.map(wod => ({
    id: wod.id,
    title: wod.title,
    date: wod.date,
    backgroundColor: '#CCFF00',
    borderColor: '#CCFF00',
    textColor: '#000000'
  }));

  const holidayEvents = holidays.map(h => ({
    id: `holiday_${h.date}`,
    title: h.localName,
    date: h.date,
    classNames: ['holiday-event']
  }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="wod-calendar-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2 flex items-center gap-4">
            <CalendarIcon className="w-12 h-12 md:w-16 md:h-16 text-[#CCFF00]" />
            WOD Calendar
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">
            View all workouts — click any event for details
          </p>
        </div>

        <Card className="bg-[#18181B] border-[#27272A] p-6" data-testid="calendar-card">
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={[...calendarEvents, ...holidayEvents]}
            eventClick={handleEventClick}
            height="auto"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth'
            }}
          />
          {/* Legend */}
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-[#27272A]">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm bg-[#CCFF00] inline-block" />
              <span className="text-xs text-[#A1A1AA] uppercase tracking-wide">WOD</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm bg-[#38BDF8] inline-block" />
              <span className="text-xs text-[#A1A1AA] uppercase tracking-wide">SA Public Holiday</span>
            </div>
          </div>
        </Card>
      </div>

      {/* WOD View Dialog with Rating */}
      <Dialog open={showWodDialog} onOpenChange={setShowWodDialog}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wod-detail-dialog">
          {selectedWod && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-heading font-black uppercase text-[#FAFAFA]">
                  {selectedWod.title}
                </DialogTitle>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <p className="text-sm text-[#A1A1AA]">
                    {new Date(selectedWod.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  {wodRatingSummary?.count > 0 && (
                    <div className="flex items-center gap-1.5 bg-[#09090B] border border-[#27272A] rounded-sm px-2 py-1" data-testid="wod-avg-rating">
                      <Star className="w-3.5 h-3.5 text-[#CCFF00] fill-[#CCFF00]" />
                      <span className="text-xs font-bold text-[#CCFF00]">{wodRatingSummary.average}</span>
                      <span className="text-xs text-[#52525B]">({wodRatingSummary.count} {wodRatingSummary.count === 1 ? 'rating' : 'ratings'})</span>
                    </div>
                  )}
                  {selectedWod.created_by_name && (
                    <p className="text-xs text-[#52525B]">by {selectedWod.created_by_name}</p>
                  )}
                </div>
              </DialogHeader>

              {/* WOD Description */}
              <div className="mt-4">
                <div className="text-[#FAFAFA] whitespace-pre-wrap font-mono text-sm leading-relaxed p-4 bg-[#09090B] rounded-sm border border-[#27272A]">
                  {selectedWod.description}
                </div>
              </div>

              {/* Rating section — only for past or today's WODs */}
              {selectedWod.date <= today && (
                <div className="mt-4 p-4 bg-[#09090B] rounded-sm border border-[#27272A]" data-testid="wod-rating-section">
                  <p className="text-xs text-[#A1A1AA] uppercase tracking-widest font-semibold mb-3">
                    {wodRatingSubmitted ? 'Your Rating' : 'How difficult was this WOD?'}
                  </p>
                  <div className="flex items-center gap-4">
                    <Slider
                      min={1}
                      max={10}
                      step={1}
                      data-testid="wod-calendar-rating-slider"
                      value={[wodRating]}
                      onValueChange={(val) => setWodRating(val[0])}
                      className="flex-1"
                    />
                    <span className={`text-lg font-bold w-8 text-center ${getDifficultyColor(wodRating)}`}>
                      {wodRating}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs font-semibold ${getDifficultyColor(wodRating)}`}>
                      {getDifficultyLabel(wodRating)}
                    </span>
                    <Button
                      onClick={submitWodRating}
                      disabled={ratingLoading}
                      data-testid="submit-wod-calendar-rating"
                      size="sm"
                      className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider rounded-sm hover:bg-[#B3E600] h-8 px-4 text-xs"
                    >
                      {ratingLoading ? 'Saving...' : wodRatingSubmitted ? 'Update Rating' : 'Submit Rating'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Public Holiday Detail Dialog */}
      <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-sm" data-testid="holiday-detail-dialog">
          {selectedHoliday && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-3">
                  <Flag className="w-4 h-4 text-[#38BDF8]" />
                  <span className="text-xs text-[#38BDF8] uppercase tracking-widest font-semibold">
                    SA Public Holiday
                  </span>
                </div>
                <DialogTitle className="text-3xl font-heading font-black uppercase text-[#FAFAFA] leading-tight">
                  {selectedHoliday.localName}
                </DialogTitle>
                <p className="text-sm text-[#A1A1AA] mt-2">
                  {new Date(selectedHoliday.date + 'T00:00:00').toLocaleDateString('en-ZA', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </DialogHeader>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
